<template>
  <div class="default-layout">
    <NavBar
      @return-cart-click="goToMaker"
      @return-search="uiStore.setSearchStatus"
    />
    <!-- Store pages already render their own mobile cart — avoid a duplicate. -->
    <ShoppingCart v-if="cartStore.showCart && isMobile && !pageOwnsMobileCart" />
    <main>
      <slot />
    </main>
    <FooterBar v-if="!cartStore.showCart && route.name !== 'checkout'" />
  </div>
</template>

<style lang="scss" scoped>
// Sticky-footer pattern: keep the footer at the viewport bottom when a
// page (e.g. zero-results search) is shorter than one screen. `<main>`
// expands to fill remaining vertical space.
.default-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  main {
    flex: 1 0 auto;
  }
}
</style>

<script setup lang="ts">
import { STORED_CART_KEY } from "~/stores/orders";
const route = useRoute();
const uiStore = useUiStore();
const cartStore = useOrdersStore();
const makersStore = useMakersStore();
const discountStore = useDiscountStore();

const { isMobile, goToMaker } = useLayoutSetup();

const pageOwnsMobileCart = computed(() => String(route.name ?? "").startsWith("store-"));

async function init() {
  if (String(route.name).split("-")[0] === "orders") return;

  const storedAddress = localStorage.getItem("address");
  if (storedAddress) {
    cartStore.setDeliveryAddress(JSON.parse(storedAddress));
  }

  if (route.query.deal_id) {
    cartStore.setDealId(route.query.deal_id as string);
  }

  if (route.query.discount) {
    discountStore.setChosenDiscount({
      discount_code: (route.query.discount as string).toUpperCase().replace(/\s+/g, ""),
    });
  }

  // Restore cart from localStorage
  const storedCart = localStorage.getItem(STORED_CART_KEY);
  if (storedCart) {
    cartStore.setCart({ cart: JSON.parse(storedCart) });
    refreshCartWithLatestMakerData();
  }
}

async function refreshCartWithLatestMakerData() {
  try {
    const cart = cartStore.cart;
    if (!cart || !cart.maker?.slug || !cart.items?.length) return;
    // Don't refresh if we're already on the store page (maker data is already loaded)
    if (String(route.name).startsWith("store-")) return;

    // Gate on the RETURN VALUE, not on chosenMaker: the ref keeps its previous
    // value when the lookup fails, so it cannot tell a fresh maker from a stale
    // one. Undefined means the store has already handled it — cleared the cart
    // and notified, or hit a transient failure and left the cart alone to retry.
    const lookedUp = await makersStore.getMakerBySlug({
      slug: cart.maker.slug,
      makerPage: false,
    });
    if (!lookedUp) return;

    // Past the gate the lookup succeeded, so chosenMaker is this maker — use the
    // store's reactive object for the rebuild rather than the raw response.
    const freshMaker = makersStore.chosenMaker;
    if (!freshMaker) return;

    await makersStore.getDeliveryDays(freshMaker.id);
    await cartStore.recreateCart({
      items: cart.items,
      maker: freshMaker,
      date: cart.date || false,
      deal_id: cart.deal_id || null,
    });
  } catch (error) {
    console.warn("[Cart Refresh] Failed to update maker data:", error);
  }
}

onMounted(async () => {
  await init();
  uiStore.setTemplateLoading(false);
});
</script>
